export const INVITATION_NOTE_MAX_LENGTH = 2000;

const ALLOWED_TAGS = new Set(['P', 'BR', 'STRONG', 'EM', 'UL', 'OL', 'LI', 'A']);
const NORMALIZED_TAGS: Record<string, string> = { B: 'STRONG', I: 'EM', DIV: 'P' };
const RICH_TAG = /<\/?(?:p|div|br|strong|b|em|i|ul|ol|li|a)(?:\s|>|\/)/i;

function isSafeLink(value: string): boolean {
  return /^(https?:\/\/|mailto:)/i.test(value.trim());
}

/**
 * Copies `source` into `target` keeping only allowed markup. Returns true when
 * something (a tag, an attribute, a non-element node) had to be dropped, so a
 * live editor can tell "needs cleaning" apart from harmless browser wrappers
 * such as <div> or <b>, which are only renamed (KAN-563).
 */
function appendSanitized(source: Node, target: Node): boolean {
  if (source.nodeType === Node.TEXT_NODE) {
    target.appendChild(document.createTextNode(source.textContent || ''));
    return false;
  }
  if (!(source instanceof HTMLElement)) return true;

  const tagName = NORMALIZED_TAGS[source.tagName] || source.tagName;
  if (!ALLOWED_TAGS.has(tagName)) {
    Array.from(source.childNodes).forEach(child => appendSanitized(child, target));
    return true;
  }

  const clean = document.createElement(tagName.toLowerCase());
  let stripped = false;
  if (tagName === 'A') {
    const href = source.getAttribute('href') || '';
    if (isSafeLink(href)) clean.setAttribute('href', href.trim());
    else stripped = true;
    if (source.attributes.length > 1) stripped = true;
  } else if (source.attributes.length > 0) {
    stripped = true;
  }
  Array.from(source.childNodes).forEach(child => {
    if (appendSanitized(child, clean)) stripped = true;
  });
  target.appendChild(clean);
  return stripped;
}

/** Browser-side preview sanitizer; the API repeats this boundary check. */
export function sanitizeInvitationNoteHtml(value: string): string {
  const source = document.createElement('div');
  // Values without supported markup are legacy/plain text and must not be
  // interpreted as HTML merely because they contain angle brackets.
  if (!RICH_TAG.test(value)) source.textContent = value;
  else source.innerHTML = value;
  return sanitizeInvitationNoteNodes(source).html;
}

/**
 * Sanitizes a live DOM subtree (the contenteditable box itself). Unlike the
 * string form there is no legacy-plain-text guess: the editor's contents are
 * always markup, so `&nbsp;` in a text node stays a non-breaking space and is
 * never re-escaped into visible "&amp;nbsp;" text.
 */
export function sanitizeInvitationNoteNodes(root: Node): { html: string; stripped: boolean } {
  const target = document.createElement('div');
  let stripped = false;
  Array.from(root.childNodes).forEach(child => {
    if (appendSanitized(child, target)) stripped = true;
  });
  return { html: target.innerHTML.trim(), stripped };
}

function nodeText(node: Node, listIndex?: number): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent || '';
  if (!(node instanceof HTMLElement)) return '';
  if (node.tagName === 'BR') return '\n';

  const children = Array.from(node.childNodes)
    .map(child => nodeText(child))
    .join('');
  if (node.tagName === 'LI') {
    return `${listIndex === undefined ? '- ' : `${listIndex}. `}${children}\n`;
  }
  if (node.tagName === 'P') return `${children}\n\n`;
  return children;
}

export function invitationNoteText(value: string): string {
  const sanitized = sanitizeInvitationNoteHtml(value);
  const root = document.createElement('div');
  root.innerHTML = sanitized;
  const text = Array.from(root.childNodes).map(node => {
    if (node instanceof HTMLOListElement) {
      return Array.from(node.children).map((child, index) => nodeText(child, index + 1)).join('');
    }
    return nodeText(node);
  }).join('');
  return text.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

export function invitationNoteLength(value: string): number {
  return Array.from(invitationNoteText(value)).length;
}
